import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  ChevronDown, ChevronRight, Check, Save, User, Activity,
  Pill, TreePine, AlertTriangle, Users, FileText, Brain,
  Clock, Plus, Trash2, History, ClipboardCheck, Upload, FileSpreadsheet, Sparkles, Loader2
} from 'lucide-react';

/* ── Section metadata ──────────────────────────────── */
const SECTION_META = [
  { key: 'identity', label: 'Identity + Condition', icon: User, color: '#C89B7B' },
  { key: 'symptoms', label: 'Symptom Intelligence', icon: Activity, color: '#7BA3C8' },
  { key: 'symptom_verification', label: 'Symptom Verification', icon: ClipboardCheck, color: '#7BC89B' },
  { key: 'medication_response', label: 'Medication Response', icon: Pill, color: '#E07B7B' },
  { key: 'terrain', label: 'Biological Terrain', icon: TreePine, color: '#4E7A6B' },
  { key: 'exposure', label: 'Exposure History', icon: AlertTriangle, color: '#C8A87B' },
  { key: 'family', label: 'Family Pattern', icon: Users, color: '#9B7BC8' },
  { key: 'clinical_notes', label: 'Clinical Notes', icon: FileText, color: '#7BADC8' },
  { key: 'interpretation', label: 'Biological Interpretation', icon: Brain, color: '#C87BAD' },
];

/* ── Subscriber selector ───────────────────────────── */
function SubscriberPicker({ subscribers, selected, onSelect }) {
  return (
    <Select value={selected} onValueChange={onSelect}>
      <SelectTrigger className="bg-adm-panel border-white/10 text-adm-primary w-72" data-testid="subscriber-picker">
        <SelectValue placeholder="Select subscriber" />
      </SelectTrigger>
      <SelectContent className="bg-adm-panel border-white/10">
        {subscribers.map(s => (
          <SelectItem key={s.id} value={s.id} className="text-adm-primary focus:bg-white/5">
            {s.name} ({s.email})
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/* ── Severity slider with label ────────────────────── */
function SeveritySlider({ label, value, onChange, max = 10 }) {
  const sev = value || 0;
  const color = sev <= 3 ? '#4E7A6B' : sev <= 6 ? '#C8A87B' : '#E07B7B';
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-adm-secondary text-xs">{label}</Label>
        <span className="text-xs font-mono" style={{ color }}>{sev}/{max}</span>
      </div>
      <Slider
        min={0} max={max} step={1} value={[sev]}
        onValueChange={([v]) => onChange(v)}
        className="w-full"
      />
    </div>
  );
}

/* ── Section: Identity + Condition ─────────────────── */
function IdentitySection({ data, onChange }) {
  const d = data || {};
  const set = (k, v) => onChange({ ...d, [k]: v });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="space-y-1.5">
        <Label className="text-adm-secondary text-xs">Preferred Name</Label>
        <Input value={d.preferred_name || ''} onChange={e => set('preferred_name', e.target.value)}
          className="bg-adm-bg border-white/10 text-adm-primary" placeholder="How subscriber prefers to be called" data-testid="identity-preferred-name" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1.5">
          <Label className="text-adm-secondary text-xs">Age</Label>
          <Input type="number" value={d.age || ''} onChange={e => set('age', e.target.value ? parseInt(e.target.value) : null)}
            className="bg-adm-bg border-white/10 text-adm-primary" placeholder="e.g. 62" data-testid="identity-age" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-adm-secondary text-xs">Gender</Label>
          <Select value={d.gender || ''} onValueChange={v => set('gender', v)}>
            <SelectTrigger className="bg-adm-bg border-white/10 text-adm-primary" data-testid="identity-gender"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="bg-adm-panel border-white/10">
              {['Male', 'Female', 'Non-binary', 'Prefer not to say'].map(t => (
                <SelectItem key={t} value={t} className="text-adm-primary focus:bg-white/5">{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-adm-secondary text-xs">Diagnosis Type</Label>
        <Select value={d.diagnosis_type || ''} onValueChange={v => set('diagnosis_type', v)}>
          <SelectTrigger className="bg-adm-bg border-white/10 text-adm-primary" data-testid="identity-diagnosis-type">
            <SelectValue placeholder="Select type" />
          </SelectTrigger>
          <SelectContent className="bg-adm-panel border-white/10">
            {['Idiopathic PD', 'Young-Onset PD', 'Atypical Parkinsonism', 'MSA', 'PSP', 'DLB', 'CBD', 'Other'].map(t => (
              <SelectItem key={t} value={t} className="text-adm-primary focus:bg-white/5">{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label className="text-adm-secondary text-xs">Current Stage</Label>
        <Select value={d.current_stage || ''} onValueChange={v => set('current_stage', v)}>
          <SelectTrigger className="bg-adm-bg border-white/10 text-adm-primary" data-testid="identity-current-stage">
            <SelectValue placeholder="Select stage" />
          </SelectTrigger>
          <SelectContent className="bg-adm-panel border-white/10">
            {['Early', 'Mild', 'Moderate', 'Advanced', 'Late'].map(t => (
              <SelectItem key={t} value={t} className="text-adm-primary focus:bg-white/5">{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label className="text-adm-secondary text-xs">Onset Year</Label>
        <Input type="number" value={d.onset_year || ''} onChange={e => set('onset_year', e.target.value ? parseInt(e.target.value) : null)}
          className="bg-adm-bg border-white/10 text-adm-primary" placeholder="e.g. 2018" data-testid="identity-onset-year" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-adm-secondary text-xs">Diagnosis Year</Label>
        <Input type="number" value={d.diagnosis_year || ''} onChange={e => set('diagnosis_year', e.target.value ? parseInt(e.target.value) : null)}
          className="bg-adm-bg border-white/10 text-adm-primary" placeholder="e.g. 2020" data-testid="identity-diagnosis-year" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-adm-secondary text-xs">Progression Velocity</Label>
        <Select value={d.progression_velocity || ''} onValueChange={v => set('progression_velocity', v)}>
          <SelectTrigger className="bg-adm-bg border-white/10 text-adm-primary">
            <SelectValue placeholder="Select" />
          </SelectTrigger>
          <SelectContent className="bg-adm-panel border-white/10">
            {['Slow', 'Moderate', 'Fast', 'Variable', 'Unknown'].map(t => (
              <SelectItem key={t} value={t} className="text-adm-primary focus:bg-white/5">{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label className="text-adm-secondary text-xs">Stability State</Label>
        <Select value={d.stability_state || ''} onValueChange={v => set('stability_state', v)}>
          <SelectTrigger className="bg-adm-bg border-white/10 text-adm-primary">
            <SelectValue placeholder="Select" />
          </SelectTrigger>
          <SelectContent className="bg-adm-panel border-white/10">
            {['Stable', 'Fluctuating', 'Declining', 'Improving'].map(t => (
              <SelectItem key={t} value={t} className="text-adm-primary focus:bg-white/5">{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label className="text-adm-secondary text-xs">Functional Status</Label>
        <Input value={d.functional_status || ''} onChange={e => set('functional_status', e.target.value)}
          className="bg-adm-bg border-white/10 text-adm-primary" placeholder="e.g. Independent with mild limitations" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-adm-secondary text-xs">Primary Limitation</Label>
        <Input value={d.primary_limitation || ''} onChange={e => set('primary_limitation', e.target.value)}
          className="bg-adm-bg border-white/10 text-adm-primary" placeholder="e.g. Fine motor tasks" />
      </div>
      <div className="md:col-span-2 space-y-1.5">
        <Label className="text-adm-secondary text-xs">Condition Summary</Label>
        <Textarea value={d.condition_summary || ''} onChange={e => set('condition_summary', e.target.value)}
          className="bg-adm-bg border-white/10 text-adm-primary text-xs resize-none" rows={2}
          placeholder="Brief overview of the subscriber's condition and current state" data-testid="identity-condition-summary" />
      </div>
    </div>
  );
}

/* ── Section: Symptom Intelligence ─────────────────── */
function SymptomsSection({ data, onChange }) {
  const d = data || { motor: {}, non_motor: {} };
  const motor = d.motor || {};
  const nonMotor = d.non_motor || {};
  const setMotor = (k, field, v) => {
    const updated = { ...d, motor: { ...motor, [k]: { ...(motor[k] || {}), [field]: v } } };
    onChange(updated);
  };
  const setNonMotor = (k, field, v) => {
    const updated = { ...d, non_motor: { ...nonMotor, [k]: { ...(nonMotor[k] || {}), [field]: v } } };
    onChange(updated);
  };
  const setField = (k, v) => onChange({ ...d, [k]: v });

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-sm font-medium text-adm-primary mb-3 font-['Work_Sans']">Motor Symptoms</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { key: 'tremor', label: 'Tremor', extra: 'side', extraLabel: 'Side', opts: ['Left', 'Right', 'Bilateral'] },
            { key: 'rigidity', label: 'Rigidity', extra: 'distribution', extraLabel: 'Distribution', opts: ['Axial', 'Appendicular', 'Both'] },
            { key: 'bradykinesia', label: 'Bradykinesia', extra: 'worst_task', extraLabel: 'Worst Task', isText: true },
            { key: 'freezing', label: 'Freezing', extra: 'trigger', extraLabel: 'Trigger', isText: true },
            { key: 'dyskinesia', label: 'Dyskinesia', extra: 'type', extraLabel: 'Type', opts: ['Peak-dose', 'End-of-dose', 'Biphasic', 'Off-period'] },
          ].map(({ key, label, extra, extraLabel, opts, isText }) => (
            <Card key={key} className="bg-adm-bg/50 border-white/5 p-3">
              <SeveritySlider label={label} value={(motor[key] || {}).severity} onChange={v => setMotor(key, 'severity', v)} />
              <div className="mt-2">
                <Label className="text-adm-tertiary text-[10px]">{extraLabel}</Label>
                {isText ? (
                  <Input value={(motor[key] || {})[extra] || ''} onChange={e => setMotor(key, extra, e.target.value)}
                    className="bg-adm-bg border-white/8 text-adm-primary text-xs h-7 mt-0.5" placeholder={`Enter ${extraLabel.toLowerCase()}`} />
                ) : (
                  <Select value={(motor[key] || {})[extra] || ''} onValueChange={v => setMotor(key, extra, v)}>
                    <SelectTrigger className="bg-adm-bg border-white/8 text-adm-primary text-xs h-7 mt-0.5"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="bg-adm-panel border-white/10">
                      {opts.map(o => <SelectItem key={o} value={o} className="text-adm-primary focus:bg-white/5 text-xs">{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </Card>
          ))}
        </div>
      </div>
      <div>
        <h4 className="text-sm font-medium text-adm-primary mb-3 font-['Work_Sans']">Non-Motor Symptoms</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { key: 'cognition', label: 'Cognition' },
            { key: 'sleep', label: 'Sleep' },
            { key: 'autonomic', label: 'Autonomic' },
            { key: 'mood', label: 'Mood' },
          ].map(({ key, label }) => (
            <Card key={key} className="bg-adm-bg/50 border-white/5 p-3">
              <SeveritySlider label={label} value={(nonMotor[key] || {}).severity} onChange={v => setNonMotor(key, 'severity', v)} />
            </Card>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-1.5">
          <Label className="text-adm-secondary text-xs">Worst Time of Day</Label>
          <Select value={d.worst_time || ''} onValueChange={v => setField('worst_time', v)}>
            <SelectTrigger className="bg-adm-bg border-white/10 text-adm-primary"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="bg-adm-panel border-white/10">
              {['Morning', 'Midday', 'Afternoon', 'Evening', 'Night', 'Variable'].map(t => (
                <SelectItem key={t} value={t} className="text-adm-primary focus:bg-white/5">{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-adm-secondary text-xs">Progression Trend</Label>
          <Select value={d.progression_trend || ''} onValueChange={v => setField('progression_trend', v)}>
            <SelectTrigger className="bg-adm-bg border-white/10 text-adm-primary"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="bg-adm-panel border-white/10">
              {['Stable', 'Slowly Worsening', 'Rapidly Worsening', 'Improving', 'Fluctuating'].map(t => (
                <SelectItem key={t} value={t} className="text-adm-primary focus:bg-white/5">{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-adm-secondary text-xs">Asymmetry</Label>
          <Select value={d.asymmetry || ''} onValueChange={v => setField('asymmetry', v)}>
            <SelectTrigger className="bg-adm-bg border-white/10 text-adm-primary"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="bg-adm-panel border-white/10">
              {['Left-dominant', 'Right-dominant', 'Bilateral symmetric', 'Bilateral asymmetric'].map(t => (
                <SelectItem key={t} value={t} className="text-adm-primary focus:bg-white/5">{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

/* ── Section: Symptom Verification ──────────────────── */
function SymptomVerificationSection({ data, onChange }) {
  const d = data || {};
  const set = (k, v) => onChange({ ...d, [k]: v });
  const VERIFICATION_ITEMS = [
    { key: 'slowness_side', label: 'Slowness (side)', description: 'Which side is more affected by slowness/bradykinesia?', type: 'select', options: ['Left', 'Right', 'Both equal', 'Variable'] },
    { key: 'gait_issue', label: 'Gait Issue', description: 'Walking pattern, stride length, shuffling', type: 'select', options: ['Normal', 'Mild shuffle', 'Moderate difficulty', 'Severe / freezing', 'Uses walking aid'] },
    { key: 'toe_curling_dystonia', label: 'Toe Curling / Dystonia', description: 'Involuntary muscle contractions, especially in feet', type: 'select', options: ['None', 'Occasional', 'Frequent - morning', 'Frequent - OFF periods', 'Constant'] },
    { key: 'speech_worsening', label: 'Speech Worsening', description: 'Voice volume, clarity, speed changes', type: 'select', options: ['Normal', 'Softer voice', 'Slurred occasionally', 'Significant difficulty', 'Barely audible at times'] },
    { key: 'posture_issue', label: 'Posture Issue', description: 'Forward lean, stooped posture, camptocormia', type: 'select', options: ['Normal', 'Mild forward lean', 'Noticeable stoop', 'Severe lean', 'Pisa syndrome'] },
    { key: 'balance_issue', label: 'Balance Issue', description: 'Stability when standing, turning, or pushed', type: 'select', options: ['Normal', 'Occasional unsteadiness', 'Frequent near-falls', 'Falls monthly', 'Falls weekly or more'] },
    { key: 'facial_masking', label: 'Facial Masking', description: 'Reduced facial expression (hypomimia)', type: 'select', options: ['Normal expression', 'Mildly reduced', 'Noticeably flat', 'Severe masking'] },
    { key: 'internal_tremor', label: 'Internal Tremor', description: 'Sensation of trembling inside the body not visible externally', type: 'select', options: ['None', 'Occasional', 'Frequent', 'Near-constant', 'Wakes from sleep'] },
    { key: 'constipation_vs_onset', label: 'Constipation Timing vs Onset', description: 'When did constipation begin relative to PD diagnosis?', type: 'select', options: ['Before diagnosis (years)', 'Before diagnosis (months)', 'Around same time', 'After diagnosis', 'No constipation'] },
  ];
  return (
    <div className="space-y-3">
      <p className="text-adm-tertiary text-[10px] italic mb-2">Specific Parkinson's symptom verification for accurate clinical profiling</p>
      <div className="grid grid-cols-1 gap-3">
        {VERIFICATION_ITEMS.map(item => (
          <Card key={item.key} className="bg-adm-bg/50 border-white/5 p-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 items-start">
              <div>
                <Label className="text-adm-secondary text-xs font-medium">{item.label}</Label>
                <p className="text-adm-tertiary text-[10px] mt-0.5">{item.description}</p>
              </div>
              <Select value={d[item.key] || ''} onValueChange={v => set(item.key, v)}>
                <SelectTrigger className="bg-adm-bg border-white/8 text-adm-primary text-xs" data-testid={`verify-${item.key}`}>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="bg-adm-panel border-white/10">
                  {item.options.map(o => (
                    <SelectItem key={o} value={o} className="text-adm-primary focus:bg-white/5 text-xs">{o}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}


/* ── Section: Medication Response (CRITICAL) ───────── */
function MedicationResponseSection({ data, onChange }) {
  const d = data || { current_medications: [] };
  const set = (k, v) => onChange({ ...d, [k]: v });
  const meds = d.current_medications || [];

  const addMed = () => {
    onChange({ ...d, current_medications: [...meds, { name: '', dose: '', frequency: '', since: '', notes: '' }] });
  };
  const updateMed = (idx, field, val) => {
    const updated = [...meds];
    updated[idx] = { ...updated[idx], [field]: val };
    onChange({ ...d, current_medications: updated });
  };
  const removeMed = (idx) => {
    onChange({ ...d, current_medications: meds.filter((_, i) => i !== idx) });
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-1.5">
          <Label className="text-adm-secondary text-xs">ON Latency (minutes)</Label>
          <Input type="number" value={d.on_latency_minutes ?? ''} onChange={e => set('on_latency_minutes', e.target.value ? parseInt(e.target.value) : null)}
            className="bg-adm-bg border-white/10 text-adm-primary" placeholder="e.g. 30" data-testid="med-on-latency" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-adm-secondary text-xs">ON Duration (hours)</Label>
          <Input type="number" step="0.5" value={d.on_duration_hours ?? ''} onChange={e => set('on_duration_hours', e.target.value ? parseFloat(e.target.value) : null)}
            className="bg-adm-bg border-white/10 text-adm-primary" placeholder="e.g. 3.5" data-testid="med-on-duration" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-adm-secondary text-xs">ON Quality</Label>
          <Select value={d.on_quality || ''} onValueChange={v => set('on_quality', v)}>
            <SelectTrigger className="bg-adm-bg border-white/10 text-adm-primary" data-testid="med-on-quality"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="bg-adm-panel border-white/10">
              {['Excellent', 'Good', 'Fair', 'Poor', 'Minimal'].map(t => (
                <SelectItem key={t} value={t} className="text-adm-primary focus:bg-white/5">{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-adm-secondary text-xs">OFF Pattern</Label>
          <Select value={d.off_pattern || ''} onValueChange={v => set('off_pattern', v)}>
            <SelectTrigger className="bg-adm-bg border-white/10 text-adm-primary"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="bg-adm-panel border-white/10">
              {['Predictable', 'Unpredictable', 'Morning OFF', 'Night OFF', 'Random'].map(t => (
                <SelectItem key={t} value={t} className="text-adm-primary focus:bg-white/5">{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-adm-secondary text-xs">Wearing Off Pattern</Label>
          <Select value={d.wearing_off_pattern || ''} onValueChange={v => set('wearing_off_pattern', v)}>
            <SelectTrigger className="bg-adm-bg border-white/10 text-adm-primary"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="bg-adm-panel border-white/10">
              {['None', 'Mild end-of-dose', 'Moderate', 'Severe', 'Dose failures'].map(t => (
                <SelectItem key={t} value={t} className="text-adm-primary focus:bg-white/5">{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-adm-secondary text-xs">Dystonia Pattern</Label>
          <Input value={d.dystonia_pattern || ''} onChange={e => set('dystonia_pattern', e.target.value)}
            className="bg-adm-bg border-white/10 text-adm-primary" placeholder="e.g. Early morning foot dystonia" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-adm-secondary text-xs">Rescue Dependency</Label>
          <Select value={d.rescue_dependency || ''} onValueChange={v => set('rescue_dependency', v)}>
            <SelectTrigger className="bg-adm-bg border-white/10 text-adm-primary"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="bg-adm-panel border-white/10">
              {['None', 'Occasional', 'Frequent', 'Daily', 'Multiple daily'].map(t => (
                <SelectItem key={t} value={t} className="text-adm-primary focus:bg-white/5">{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {/* Current medications list */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-adm-primary font-['Work_Sans']">Current Medications</h4>
          <Button size="sm" variant="outline" onClick={addMed} className="border-white/10 text-adm-secondary hover:text-adm-primary h-7 text-xs" data-testid="add-medication-btn">
            <Plus className="w-3 h-3 mr-1" /> Add
          </Button>
        </div>
        {meds.length === 0 && (
          <p className="text-adm-tertiary text-xs italic py-4 text-center">No medications added yet</p>
        )}
        <div className="space-y-2">
          {meds.map((med, idx) => (
            <Card key={idx} className="bg-adm-bg/50 border-white/5 p-3">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
                <div className="space-y-1">
                  <Label className="text-adm-tertiary text-[10px]">Name</Label>
                  <Input value={med.name || ''} onChange={e => updateMed(idx, 'name', e.target.value)}
                    className="bg-adm-bg border-white/8 text-adm-primary text-xs h-7" placeholder="Medication" data-testid={`med-name-${idx}`} />
                </div>
                <div className="space-y-1">
                  <Label className="text-adm-tertiary text-[10px]">Dose</Label>
                  <Input value={med.dose || ''} onChange={e => updateMed(idx, 'dose', e.target.value)}
                    className="bg-adm-bg border-white/8 text-adm-primary text-xs h-7" placeholder="e.g. 100mg" />
                </div>
                <div className="space-y-1">
                  <Label className="text-adm-tertiary text-[10px]">Frequency</Label>
                  <Input value={med.frequency || ''} onChange={e => updateMed(idx, 'frequency', e.target.value)}
                    className="bg-adm-bg border-white/8 text-adm-primary text-xs h-7" placeholder="e.g. 3x daily" />
                </div>
                <div className="space-y-1">
                  <Label className="text-adm-tertiary text-[10px]">Since</Label>
                  <Input value={med.since || ''} onChange={e => updateMed(idx, 'since', e.target.value)}
                    className="bg-adm-bg border-white/8 text-adm-primary text-xs h-7" placeholder="e.g. 2020" />
                </div>
                <div className="flex items-end justify-end">
                  <Button size="icon" variant="ghost" onClick={() => removeMed(idx)} className="text-adm-tertiary hover:text-red-400 h-7 w-7">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Section: Biological Terrain ───────────────────── */
function TerrainSection({ data, onChange }) {
  const d = data || {};
  const gut = d.gut || {};
  const sleep = d.sleep || {};
  const stress = d.stress || {};
  const energy = d.energy || {};

  const setGut = (k, v) => onChange({ ...d, gut: { ...gut, [k]: v } });
  const setSleep = (k, v) => onChange({ ...d, sleep: { ...sleep, [k]: v } });
  const setStress = (k, v) => onChange({ ...d, stress: { ...stress, [k]: v } });
  const setEnergy = (k, v) => onChange({ ...d, energy: { ...energy, [k]: v } });

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-sm font-medium text-adm-primary mb-3 font-['Work_Sans']">Gut Health</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SeveritySlider label="Constipation Severity" value={gut.constipation_severity} onChange={v => setGut('constipation_severity', v)} />
          <SeveritySlider label="Bloating" value={gut.bloating} onChange={v => setGut('bloating', v)} />
          <div className="space-y-1.5">
            <Label className="text-adm-secondary text-xs">Bowel Frequency</Label>
            <Select value={gut.bowel_frequency || ''} onValueChange={v => setGut('bowel_frequency', v)}>
              <SelectTrigger className="bg-adm-bg border-white/10 text-adm-primary"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent className="bg-adm-panel border-white/10">
                {['Multiple daily', 'Daily', 'Every other day', 'Twice a week', 'Once a week', 'Less than weekly'].map(t => (
                  <SelectItem key={t} value={t} className="text-adm-primary focus:bg-white/5">{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-adm-secondary text-xs">Digestion Quality</Label>
            <Select value={gut.digestion_quality || ''} onValueChange={v => setGut('digestion_quality', v)}>
              <SelectTrigger className="bg-adm-bg border-white/10 text-adm-primary"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent className="bg-adm-panel border-white/10">
                {['Good', 'Fair', 'Poor', 'Very Poor'].map(t => (
                  <SelectItem key={t} value={t} className="text-adm-primary focus:bg-white/5">{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-adm-secondary text-xs">Gut Notes</Label>
            <Textarea value={gut.notes || ''} onChange={e => setGut('notes', e.target.value)}
              className="bg-adm-bg border-white/10 text-adm-primary text-xs resize-none" rows={2} placeholder="Additional gut observations" />
          </div>
        </div>
      </div>
      <div>
        <h4 className="text-sm font-medium text-adm-primary mb-3 font-['Work_Sans']">Sleep Terrain</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-adm-secondary text-xs">Total Hours</Label>
            <Input type="number" step="0.5" value={sleep.total_hours ?? ''} onChange={e => setSleep('total_hours', e.target.value ? parseFloat(e.target.value) : null)}
              className="bg-adm-bg border-white/10 text-adm-primary" placeholder="e.g. 6.5" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-adm-secondary text-xs">Awakenings per night</Label>
            <Input type="number" value={sleep.awakenings ?? ''} onChange={e => setSleep('awakenings', e.target.value ? parseInt(e.target.value) : null)}
              className="bg-adm-bg border-white/10 text-adm-primary" placeholder="e.g. 3" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-adm-secondary text-xs">Depth Perception</Label>
            <Select value={sleep.depth_perception || ''} onValueChange={v => setSleep('depth_perception', v)}>
              <SelectTrigger className="bg-adm-bg border-white/10 text-adm-primary"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent className="bg-adm-panel border-white/10">
                {['Deep', 'Moderate', 'Light', 'Very Light'].map(t => (
                  <SelectItem key={t} value={t} className="text-adm-primary focus:bg-white/5">{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-adm-secondary text-xs">Sleep Notes</Label>
            <Textarea value={sleep.notes || ''} onChange={e => setSleep('notes', e.target.value)}
              className="bg-adm-bg border-white/10 text-adm-primary text-xs resize-none" rows={2} />
          </div>
        </div>
      </div>
      <div>
        <h4 className="text-sm font-medium text-adm-primary mb-3 font-['Work_Sans']">Stress + Energy</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SeveritySlider label="Baseline Stress Level" value={stress.baseline_level} onChange={v => setStress('baseline_level', v)} />
          <div className="space-y-1.5">
            <Label className="text-adm-secondary text-xs">Acute Triggers</Label>
            <Input value={stress.acute_triggers || ''} onChange={e => setStress('acute_triggers', e.target.value)}
              className="bg-adm-bg border-white/10 text-adm-primary" placeholder="e.g. Family situations, work pressure" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-adm-secondary text-xs">Stress Timeline</Label>
            <Textarea value={stress.stress_timeline || ''} onChange={e => setStress('stress_timeline', e.target.value)}
              className="bg-adm-bg border-white/10 text-adm-primary text-xs resize-none" rows={2}
              placeholder="Timeline of major stressors leading to or after diagnosis" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-adm-secondary text-xs mb-2 block">Energy Pattern (0-10)</Label>
            <div className="grid grid-cols-3 gap-4">
              <SeveritySlider label="Morning" value={energy.morning} onChange={v => setEnergy('morning', v)} />
              <SeveritySlider label="Afternoon" value={energy.afternoon} onChange={v => setEnergy('afternoon', v)} />
              <SeveritySlider label="Evening" value={energy.evening} onChange={v => setEnergy('evening', v)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-adm-secondary text-xs">Energy Crash Pattern</Label>
            <Input value={energy.crash_pattern || ''} onChange={e => setEnergy('crash_pattern', e.target.value)}
              className="bg-adm-bg border-white/10 text-adm-primary" placeholder="e.g. Post-lunch crash at 2pm" />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Section: Exposure History ─────────────────────── */
function ExposureSection({ data, onChange }) {
  const d = data || {};
  const set = (k, v) => onChange({ ...d, [k]: v });
  return (
    <div className="space-y-4">
      {[
        { key: 'toxin_exposure', label: 'Toxin Exposure', detailKey: 'toxin_details' },
        { key: 'head_injury', label: 'Head Injury', detailKey: 'head_injury_details' },
        { key: 'pesticide_exposure', label: 'Pesticide Exposure', detailKey: 'pesticide_details' },
      ].map(({ key, label, detailKey }) => (
        <Card key={key} className="bg-adm-bg/50 border-white/5 p-3">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-adm-secondary text-xs">{label}</Label>
            <Switch checked={!!d[key]} onCheckedChange={v => set(key, v)} data-testid={`exposure-${key}`} />
          </div>
          {d[key] && (
            <Textarea value={d[detailKey] || ''} onChange={e => set(detailKey, e.target.value)}
              className="bg-adm-bg border-white/8 text-adm-primary text-xs resize-none" rows={2} placeholder="Details..." />
          )}
        </Card>
      ))}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-adm-secondary text-xs">Antibiotic History</Label>
          <Select value={d.antibiotic_history || ''} onValueChange={v => set('antibiotic_history', v)}>
            <SelectTrigger className="bg-adm-bg border-white/10 text-adm-primary"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="bg-adm-panel border-white/10">
              {['None significant', 'Occasional', 'Frequent', 'Prolonged/Heavy'].map(t => (
                <SelectItem key={t} value={t} className="text-adm-primary focus:bg-white/5">{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-adm-secondary text-xs">Occupational Risk</Label>
          <Input value={d.occupational_risk || ''} onChange={e => set('occupational_risk', e.target.value)}
            className="bg-adm-bg border-white/10 text-adm-primary" placeholder="e.g. Chemical factory worker" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-adm-secondary text-xs">Exposure Notes</Label>
        <Textarea value={d.notes || ''} onChange={e => set('notes', e.target.value)}
          className="bg-adm-bg border-white/10 text-adm-primary text-xs resize-none" rows={2} />
      </div>
    </div>
  );
}

/* ── Section: Family Pattern ───────────────────────── */
function FamilySection({ data, onChange }) {
  const d = data || {};
  const set = (k, v) => onChange({ ...d, [k]: v });
  return (
    <div className="space-y-4">
      <Card className="bg-adm-bg/50 border-white/5 p-3">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-adm-secondary text-xs">Parkinson's in Family</Label>
          <Switch checked={!!d.pd_in_family} onCheckedChange={v => set('pd_in_family', v)} data-testid="family-pd" />
        </div>
        {d.pd_in_family && (
          <Textarea value={d.pd_details || ''} onChange={e => set('pd_details', e.target.value)}
            className="bg-adm-bg border-white/8 text-adm-primary text-xs resize-none" rows={2} placeholder="Which family members? Relationship and age of onset" />
        )}
      </Card>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { key: 'neurological', label: 'Neurological Conditions' },
          { key: 'autoimmune', label: 'Autoimmune Conditions' },
          { key: 'metabolic', label: 'Metabolic Conditions' },
        ].map(({ key, label }) => (
          <div key={key} className="space-y-1.5">
            <Label className="text-adm-secondary text-xs">{label}</Label>
            <Input value={(d[key] || []).join(', ')} onChange={e => set(key, e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
              className="bg-adm-bg border-white/10 text-adm-primary text-xs" placeholder="Comma separated" />
          </div>
        ))}
      </div>
      <div className="space-y-1.5">
        <Label className="text-adm-secondary text-xs">Generation Pattern</Label>
        <Input value={d.generation_pattern || ''} onChange={e => set('generation_pattern', e.target.value)}
          className="bg-adm-bg border-white/10 text-adm-primary" placeholder="e.g. Skip-generation pattern observed" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-adm-secondary text-xs">Family Notes</Label>
        <Textarea value={d.notes || ''} onChange={e => set('notes', e.target.value)}
          className="bg-adm-bg border-white/10 text-adm-primary text-xs resize-none" rows={2} />
      </div>
    </div>
  );
}

/* ── Section: Clinical Notes ───────────────────────── */
function ClinicalNotesSection({ data, subscriberId, onRefresh }) {
  const [noteText, setNoteText] = useState('');
  const [saving, setSaving] = useState(false);
  const notes = Array.isArray(data) ? data : [];

  const addNote = async () => {
    if (!noteText.trim()) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/biological-context/${subscriberId}/section/clinical_notes`, {
        data: { text: noteText }
      });
      setNoteText('');
      toast.success('Note added');
      onRefresh();
    } catch (err) {
      toast.error('Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/biological-context/${subscriberId}/notes/${noteId}`);
      toast.success('Note deleted');
      onRefresh();
    } catch (err) {
      toast.error('Failed to delete note');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Textarea value={noteText} onChange={e => setNoteText(e.target.value)}
          className="bg-adm-bg border-white/10 text-adm-primary text-xs resize-none flex-1"
          rows={2} placeholder="Add clinical observation..." data-testid="clinical-note-input" />
        <Button onClick={addNote} disabled={saving || !noteText.trim()} className="bg-adm-accent/15 text-adm-accent border-0 hover:bg-adm-accent/25 self-end" data-testid="add-note-btn">
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      {notes.length === 0 && (
        <p className="text-adm-tertiary text-xs italic py-6 text-center">No clinical notes recorded</p>
      )}
      <div className="space-y-2">
        {notes.slice().reverse().map((note, idx) => (
          <Card key={note.id || idx} className="bg-adm-bg/50 border-white/5 p-3">
            <div className="flex justify-between items-start gap-2">
              <div className="flex-1">
                <p className="text-adm-primary text-xs leading-relaxed">{note.text}</p>
                <p className="text-adm-tertiary text-[10px] mt-1">
                  <Clock className="w-2.5 h-2.5 inline mr-0.5" />
                  {note.created_at ? new Date(note.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                </p>
              </div>
              {note.id && (
                <Button size="icon" variant="ghost" onClick={() => deleteNote(note.id)} className="text-adm-tertiary hover:text-red-400 h-6 w-6">
                  <Trash2 className="w-3 h-3" />
                </Button>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ── Section: Biological Interpretation ────────────── */
function InterpretationSection({ data, onChange }) {
  const d = data || { top_drivers: ['', '', ''] };
  const set = (k, v) => onChange({ ...d, [k]: v });
  const drivers = d.top_drivers || ['', '', ''];
  const setDriver = (idx, val) => {
    const updated = [...drivers];
    updated[idx] = val;
    set('top_drivers', updated);
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-adm-secondary text-xs mb-2 block">Top 3 Biological Drivers</Label>
        <div className="space-y-2">
          {[0, 1, 2].map(i => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-adm-accent text-xs font-mono w-4">{i + 1}.</span>
              <Input value={drivers[i] || ''} onChange={e => setDriver(i, e.target.value)}
                className="bg-adm-bg border-white/10 text-adm-primary text-xs"
                placeholder={`Driver ${i + 1}`} data-testid={`interp-driver-${i}`} />
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-adm-secondary text-xs">Primary System Stress</Label>
          <Select value={d.primary_system_stress || ''} onValueChange={v => set('primary_system_stress', v)}>
            <SelectTrigger className="bg-adm-bg border-white/10 text-adm-primary" data-testid="interp-primary-stress"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="bg-adm-panel border-white/10">
              {['Mitochondrial', 'Inflammatory', 'Neurotoxic', 'Gut-Brain Axis', 'Detox Overload', 'Metabolic', 'Autonomic'].map(t => (
                <SelectItem key={t} value={t} className="text-adm-primary focus:bg-white/5">{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-adm-secondary text-xs">Stability Level</Label>
          <Select value={d.stability_level || ''} onValueChange={v => set('stability_level', v)}>
            <SelectTrigger className="bg-adm-bg border-white/10 text-adm-primary"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="bg-adm-panel border-white/10">
              {['Stable', 'Mildly Unstable', 'Moderately Unstable', 'Unstable', 'Critical'].map(t => (
                <SelectItem key={t} value={t} className="text-adm-primary focus:bg-white/5">{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-adm-secondary text-xs">Immediate Priority</Label>
        <Textarea value={d.immediate_priority || ''} onChange={e => set('immediate_priority', e.target.value)}
          className="bg-adm-bg border-white/10 text-adm-primary text-xs resize-none" rows={3}
          placeholder="What must be addressed first in this person's biological terrain?" data-testid="interp-priority" />
      </div>
    </div>
  );
}

/* ── Main Component ────────────────────────────────── */
export default function AdminBiologicalContext() {
  const [subscribers, setSubscribers] = useState([]);
  const [selectedSub, setSelectedSub] = useState('');
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingSection, setSavingSection] = useState(null);
  const [openSections, setOpenSections] = useState({});
  const [localData, setLocalData] = useState({});
  const [versionHistory, setVersionHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importMode, setImportMode] = useState(null); // 'ai' | 'csv'
  const [importLoading, setImportLoading] = useState(false);
  const [extractedPreview, setExtractedPreview] = useState(null);

  // Fetch subscribers
  useEffect(() => {
    axios.get(`${API}/users?role=subscriber`).then(res => {
      setSubscribers(res.data || []);
    }).catch(() => {});
  }, []);

  // Fetch context when subscriber changes
  const fetchContext = useCallback(async () => {
    if (!selectedSub) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/biological-context/${selectedSub}`);
      setContext(res.data);
      if (res.data.has_context) {
        setLocalData({
          identity: res.data.identity,
          symptoms: res.data.symptoms,
          symptom_verification: res.data.symptom_verification,
          medication_response: res.data.medication_response,
          terrain: res.data.terrain,
          exposure: res.data.exposure,
          family: res.data.family,
          clinical_notes: res.data.clinical_notes,
          interpretation: res.data.interpretation,
        });
      } else {
        setLocalData({});
      }
    } catch (err) {
      toast.error('Failed to load biological context');
    } finally {
      setLoading(false);
    }
  }, [selectedSub]);

  useEffect(() => {
    fetchContext();
  }, [fetchContext]);

  // Fetch version history
  const fetchHistory = async () => {
    if (!selectedSub) return;
    try {
      const res = await axios.get(`${API}/biological-context/${selectedSub}/history`);
      setVersionHistory(res.data.versions || []);
      setShowHistory(true);
    } catch (err) {
      toast.error('Failed to load version history');
    }
  };

  // Toggle section
  const toggleSection = (key) => {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Save a section
  const saveSection = async (sectionKey) => {
    if (!selectedSub) return;
    setSavingSection(sectionKey);
    try {
      await axios.patch(`${API}/biological-context/${selectedSub}/section/${sectionKey}`, {
        data: localData[sectionKey]
      });
      toast.success(`${SECTION_META.find(s => s.key === sectionKey)?.label} saved`);
      await fetchContext();
    } catch (err) {
      toast.error(`Failed to save ${sectionKey}`);
    } finally {
      setSavingSection(null);
    }
  };

  // Create version snapshot
  const createSnapshot = async () => {
    if (!selectedSub) return;
    try {
      const res = await axios.post(`${API}/biological-context/${selectedSub}/snapshot`);
      toast.success(`Version ${res.data.version} snapshot created`);
      await fetchContext();
    } catch (err) {
      toast.error('Failed to create snapshot');
    }
  };

  // Update local data for a section
  const updateSection = (key, val) => {
    setLocalData(prev => ({ ...prev, [key]: val }));
  };

  // Update intake status
  const updateIntakeStatus = async (status) => {
    if (!selectedSub) return;
    try {
      await axios.patch(`${API}/biological-context/${selectedSub}/intake-status`, { intake_status: status });
      toast.success(`Intake marked as ${status}`);
      await fetchContext();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  // AI Extraction handler
  const handleAIExtract = async (file) => {
    if (!selectedSub || !file) return;
    setImportLoading(true);
    setExtractedPreview(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('subscriber_id', selectedSub);
      const res = await axios.post(`${API}/intake-import/ai-extract`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      setExtractedPreview(res.data);
      toast.success(`AI extracted ${res.data.field_count} fields from ${res.data.source_filename}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI extraction failed');
    } finally {
      setImportLoading(false);
    }
  };

  // CSV Import handler
  const handleCSVImport = async (file) => {
    if (!selectedSub || !file) return;
    setImportLoading(true);
    setExtractedPreview(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('subscriber_id', selectedSub);
      const res = await axios.post(`${API}/intake-import/csv-import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setExtractedPreview(res.data);
      toast.success(`Mapped ${res.data.mapped_count} of ${res.data.total_csv_columns} columns`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'CSV import failed');
    } finally {
      setImportLoading(false);
    }
  };

  // Apply extracted data to context
  const handleApplyExtraction = async () => {
    if (!selectedSub || !extractedPreview) return;
    setImportLoading(true);
    try {
      await axios.post(`${API}/intake-import/apply/${selectedSub}`, {
        extracted_data: extractedPreview.extracted_data,
        extraction_id: extractedPreview.extraction_id,
        source_type: extractedPreview.mapped_fields ? 'csv_import' : 'ai_extraction',
      });
      toast.success('Extracted data applied to biological context');
      setShowImportModal(false);
      setExtractedPreview(null);
      setImportMode(null);
      await fetchContext();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply extraction');
    } finally {
      setImportLoading(false);
    }
  };

  const completion = context?.section_completion || {};
  const completedCount = context?.sections_completed || 0;
  const totalSections = context?.sections_total || 8;
  const completionPct = context?.completion_percent || 0;
  const intakeStatus = context?.intake_status || 'draft';

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto" data-testid="biological-context-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-xl font-semibold text-adm-primary font-['Work_Sans'] tracking-tight" data-testid="page-title">
            Biological Context Engine
          </h1>
          <p className="text-adm-tertiary text-xs mt-1 font-['Work_Sans']">
            Capture the complete biological profile. Progressive saves, versioned history.
          </p>
        </div>

        {/* Subscriber Picker + Controls */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <SubscriberPicker subscribers={subscribers} selected={selectedSub} onSelect={setSelectedSub} />
          {selectedSub && context?.has_context && (
            <>
              <Button variant="outline" size="sm" onClick={createSnapshot} className="border-white/10 text-adm-secondary hover:text-adm-primary text-xs h-8" data-testid="snapshot-btn">
                <Save className="w-3 h-3 mr-1.5" /> Snapshot v{context?.version || 1}
              </Button>
              <Button variant="outline" size="sm" onClick={fetchHistory} className="border-white/10 text-adm-secondary hover:text-adm-primary text-xs h-8" data-testid="history-btn">
                <History className="w-3 h-3 mr-1.5" /> History
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setImportMode('ai'); setShowImportModal(true); setExtractedPreview(null); }}
                className="border-purple-500/20 text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 text-xs h-8" data-testid="ai-extract-btn">
                <Sparkles className="w-3 h-3 mr-1.5" /> AI Extract
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setImportMode('csv'); setShowImportModal(true); setExtractedPreview(null); }}
                className="border-emerald-500/20 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 text-xs h-8" data-testid="csv-import-btn">
                <FileSpreadsheet className="w-3 h-3 mr-1.5" /> CSV Import
              </Button>
            </>
          )}
        </div>

        {/* Completion Bar + Intake Status */}
        {selectedSub && (
          <Card className="bg-adm-panel border-white/[0.06] mb-6 p-4" data-testid="completion-bar">
            <div className="flex items-center justify-between mb-2">
              <span className="text-adm-secondary text-xs font-['Work_Sans']">Profile Completion</span>
              <div className="flex items-center gap-3">
                <Select value={intakeStatus} onValueChange={updateIntakeStatus}>
                  <SelectTrigger className={`h-6 w-24 text-[10px] border-0 px-2 ${
                    intakeStatus === 'verified' ? 'bg-emerald-500/15 text-emerald-400' :
                    intakeStatus === 'submitted' ? 'bg-blue-500/15 text-blue-400' :
                    'bg-amber-500/15 text-amber-400'
                  }`} data-testid="intake-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-adm-panel border-white/10">
                    <SelectItem value="draft" className="text-amber-400 text-xs">Draft</SelectItem>
                    <SelectItem value="submitted" className="text-blue-400 text-xs">Submitted</SelectItem>
                    <SelectItem value="verified" className="text-emerald-400 text-xs">Verified</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-adm-accent text-xs font-mono">{completedCount}/{totalSections} sections</span>
              </div>
            </div>
            <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-500 ease-out"
                style={{ width: `${completionPct}%`, backgroundColor: completionPct === 100 ? '#4E7A6B' : '#C89B7B' }} />
            </div>
          </Card>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-adm-accent border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* No subscriber selected */}
        {!selectedSub && !loading && (
          <Card className="bg-adm-panel border-white/[0.06] p-12 text-center">
            <User className="w-10 h-10 text-adm-tertiary mx-auto mb-3" strokeWidth={1} />
            <p className="text-adm-secondary text-sm font-['Work_Sans']">Select a subscriber to begin</p>
            <p className="text-adm-tertiary text-xs mt-1">The biological context captures their complete profile</p>
          </Card>
        )}

        {/* Accordion Sections */}
        {selectedSub && !loading && (
          <div className="space-y-2" data-testid="intake-sections">
            {SECTION_META.map((section) => {
              const isOpen = openSections[section.key];
              const isComplete = completion[section.key];
              const isSaving = savingSection === section.key;
              const isNotes = section.key === 'clinical_notes';

              return (
                <Card key={section.key} className="bg-adm-panel border-white/[0.06] overflow-hidden" data-testid={`section-${section.key}`}>
                  {/* Section Header (Accordion trigger) */}
                  <button
                    onClick={() => toggleSection(section.key)}
                    className="w-full flex items-center gap-3 p-4 text-left hover:bg-white/[0.02] transition-colors"
                    data-testid={`toggle-${section.key}`}
                  >
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${section.color}15` }}>
                      <section.icon className="w-3.5 h-3.5" style={{ color: section.color }} strokeWidth={1.5} />
                    </div>
                    <span className="flex-1 text-sm font-medium text-adm-primary font-['Work_Sans']">{section.label}</span>
                    {isComplete ? (
                      <Badge variant="outline" className="border-emerald-500/30 text-emerald-400 bg-emerald-500/8 text-[10px] px-2 py-0" data-testid={`complete-${section.key}`}>
                        <Check className="w-2.5 h-2.5 mr-0.5" /> Complete
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="border-white/10 text-adm-tertiary bg-transparent text-[10px] px-2 py-0">
                        Pending
                      </Badge>
                    )}
                    {isOpen ? <ChevronDown className="w-4 h-4 text-adm-tertiary" /> : <ChevronRight className="w-4 h-4 text-adm-tertiary" />}
                  </button>

                  {/* Section Body */}
                  {isOpen && (
                    <div className="px-4 pb-4 border-t border-white/[0.04]">
                      <div className="pt-4">
                        {section.key === 'identity' && <IdentitySection data={localData.identity} onChange={v => updateSection('identity', v)} />}
                        {section.key === 'symptoms' && <SymptomsSection data={localData.symptoms} onChange={v => updateSection('symptoms', v)} />}
                        {section.key === 'symptom_verification' && <SymptomVerificationSection data={localData.symptom_verification} onChange={v => updateSection('symptom_verification', v)} />}
                        {section.key === 'medication_response' && <MedicationResponseSection data={localData.medication_response} onChange={v => updateSection('medication_response', v)} />}
                        {section.key === 'terrain' && <TerrainSection data={localData.terrain} onChange={v => updateSection('terrain', v)} />}
                        {section.key === 'exposure' && <ExposureSection data={localData.exposure} onChange={v => updateSection('exposure', v)} />}
                        {section.key === 'family' && <FamilySection data={localData.family} onChange={v => updateSection('family', v)} />}
                        {section.key === 'clinical_notes' && <ClinicalNotesSection data={localData.clinical_notes} subscriberId={selectedSub} onRefresh={fetchContext} />}
                        {section.key === 'interpretation' && <InterpretationSection data={localData.interpretation} onChange={v => updateSection('interpretation', v)} />}
                      </div>
                      {/* Save button per section (except clinical_notes which saves inline) */}
                      {!isNotes && (
                        <div className="flex justify-end mt-4 pt-3 border-t border-white/[0.04]">
                          <Button onClick={() => saveSection(section.key)} disabled={isSaving}
                            className="bg-adm-accent/15 text-adm-accent border-0 hover:bg-adm-accent/25 text-xs h-8 px-4"
                            data-testid={`save-${section.key}`}>
                            {isSaving ? (
                              <div className="w-3 h-3 border-2 border-adm-accent border-t-transparent rounded-full animate-spin mr-1.5" />
                            ) : (
                              <Save className="w-3 h-3 mr-1.5" />
                            )}
                            Save {section.label}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        {/* Import Modal (AI Extract / CSV Import) */}
        <Dialog open={showImportModal} onOpenChange={(open) => { if (!open) { setShowImportModal(false); setImportMode(null); setExtractedPreview(null); } }}>
          <DialogContent className="bg-adm-surface border-white/10 text-adm-primary max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="import-modal">
            <DialogHeader>
              <DialogTitle className="text-adm-primary font-['Work_Sans'] flex items-center gap-2">
                {importMode === 'ai' ? <><Sparkles className="w-4 h-4 text-purple-400" /> AI Data Extraction</> : <><FileSpreadsheet className="w-4 h-4 text-emerald-400" /> CSV Import</>}
              </DialogTitle>
            </DialogHeader>

            {!extractedPreview ? (
              <div className="space-y-4 py-2">
                <p className="text-adm-secondary text-xs font-['Work_Sans']">
                  {importMode === 'ai'
                    ? 'Upload a document (PDF, TXT, CSV) and GPT-5.2 will extract patient intake data and map it to the biological context fields.'
                    : 'Upload a CSV file with patient data. Columns will be auto-mapped to biological context fields.'}
                </p>
                <div className="border-2 border-dashed border-white/10 rounded-xl p-8 text-center hover:border-white/20 transition-colors">
                  <Upload className="w-8 h-8 text-adm-tertiary mx-auto mb-3" strokeWidth={1} />
                  <p className="text-adm-secondary text-sm mb-2">
                    {importMode === 'ai' ? 'Drop a file here or click to upload' : 'Drop a CSV file here or click to upload'}
                  </p>
                  <p className="text-adm-tertiary text-[10px] mb-3">
                    {importMode === 'ai' ? 'PDF, TXT, CSV, DOC supported' : 'CSV files only (.csv)'}
                  </p>
                  <input
                    type="file"
                    id="import-file-input"
                    className="hidden"
                    accept={importMode === 'ai' ? '.pdf,.txt,.csv,.doc,.docx,.md' : '.csv'}
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (file) {
                        importMode === 'ai' ? handleAIExtract(file) : handleCSVImport(file);
                      }
                    }}
                    data-testid="import-file-input"
                  />
                  <Button variant="outline" size="sm" onClick={() => document.getElementById('import-file-input')?.click()}
                    disabled={importLoading}
                    className="border-white/10 text-adm-secondary hover:text-adm-primary text-xs" data-testid="import-browse-btn">
                    {importLoading ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Extracting...</> : 'Browse Files'}
                  </Button>
                </div>
                {importMode === 'csv' && (
                  <div className="bg-white/[0.03] rounded-lg p-3">
                    <p className="text-adm-secondary text-[10px] font-medium mb-1.5 uppercase tracking-wider">Expected CSV Columns</p>
                    <p className="text-adm-tertiary text-[10px] leading-relaxed">
                      name, age, gender, diagnosis_type, onset_year, diagnosis_year, current_stage, tremor_severity, rigidity_severity,
                      bradykinesia_severity, on_latency_minutes, on_duration_hours, medications, sleep_hours, constipation_severity, stress_level,
                      energy_morning, toxin_exposure, head_injury, pd_in_family, family_neurological...
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4 py-2">
                {/* Extraction results preview */}
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className="border-emerald-500/30 text-emerald-400 text-[10px]">
                    {extractedPreview.field_count || extractedPreview.mapped_count || 0} fields extracted
                  </Badge>
                  {extractedPreview.source_filename && (
                    <span className="text-adm-tertiary text-[10px]">from {extractedPreview.source_filename}</span>
                  )}
                </div>

                {/* Show mapped fields for CSV */}
                {extractedPreview.mapped_fields && (
                  <div className="bg-white/[0.03] rounded-lg p-3 max-h-40 overflow-y-auto">
                    <p className="text-adm-secondary text-[10px] font-medium mb-1.5 uppercase tracking-wider">Mapped Fields</p>
                    <div className="space-y-0.5">
                      {extractedPreview.mapped_fields.map((f, i) => (
                        <div key={i} className="flex items-center gap-2 text-[10px]">
                          <span className="text-adm-tertiary">{f.csv_column}</span>
                          <span className="text-adm-tertiary">→</span>
                          <span className="text-adm-accent">{f.mapped_to}</span>
                          <span className="text-adm-tertiary ml-auto truncate max-w-[120px]">{f.value}</span>
                        </div>
                      ))}
                    </div>
                    {extractedPreview.unmapped_fields?.length > 0 && (
                      <p className="text-amber-400/70 text-[10px] mt-2">
                        Unmapped: {extractedPreview.unmapped_fields.join(', ')}
                      </p>
                    )}
                  </div>
                )}

                {/* Show extracted data preview */}
                <div className="bg-white/[0.03] rounded-lg p-3 max-h-60 overflow-y-auto">
                  <p className="text-adm-secondary text-[10px] font-medium mb-1.5 uppercase tracking-wider">Extracted Data Preview</p>
                  {Object.entries(extractedPreview.extracted_data || {}).map(([section, fields]) => {
                    if (!fields || typeof fields !== 'object') return null;
                    const hasData = Object.values(fields).some(v => v !== null && v !== '' && v !== undefined);
                    if (!hasData) return null;
                    return (
                      <div key={section} className="mb-2">
                        <p className="text-adm-accent text-[10px] font-medium capitalize">{section.replace('_', ' ')}</p>
                        {Object.entries(fields).map(([k, v]) => {
                          if (v === null || v === '' || v === undefined) return null;
                          const display = typeof v === 'object' ? JSON.stringify(v) : String(v);
                          return (
                            <div key={k} className="flex items-center gap-2 text-[10px] pl-2">
                              <span className="text-adm-tertiary">{k}:</span>
                              <span className="text-adm-primary truncate max-w-[300px]">{display}</span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>

                <div className="flex gap-2 justify-end">
                  <Button variant="outline" size="sm" onClick={() => { setExtractedPreview(null); }}
                    className="border-white/10 text-adm-secondary text-xs" data-testid="import-discard-btn">
                    Discard
                  </Button>
                  <Button size="sm" onClick={handleApplyExtraction} disabled={importLoading}
                    className="bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 border-0 text-xs" data-testid="import-apply-btn">
                    {importLoading ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <Check className="w-3 h-3 mr-1.5" />}
                    Apply to Context
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Version History Drawer */}
        {showHistory && (
          <div className="fixed inset-0 z-50 flex justify-end" data-testid="history-panel">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowHistory(false)} />
            <div className="relative w-80 bg-adm-surface border-l border-white/[0.06] h-full overflow-y-auto p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-adm-primary text-sm font-medium font-['Work_Sans']">Version History</h3>
                <Button size="icon" variant="ghost" onClick={() => setShowHistory(false)} className="text-adm-tertiary h-7 w-7">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
              {versionHistory.length === 0 && (
                <p className="text-adm-tertiary text-xs italic">No versions yet</p>
              )}
              <div className="space-y-2">
                {versionHistory.map((v) => {
                  const sc = v.section_completion || {};
                  const done = Object.values(sc).filter(Boolean).length;
                  return (
                    <Card key={v.id} className="bg-adm-bg/50 border-white/5 p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-adm-accent text-xs font-mono">v{v.version}</span>
                        <span className="text-adm-tertiary text-[10px]">{done}/8</span>
                      </div>
                      <p className="text-adm-tertiary text-[10px]">
                        {v.updated_at ? new Date(v.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                      </p>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
